/**
 * SeatGeometryPropertiesPanel - Property readout + governed delete for a
 * selected PERSISTED (saved) canonical seat geometry.
 * Elvis Aloha Collection - Arena Map Editor
 *
 * Distinct from DraftSeatPropertiesPanel (unsaved local drafts, delete is
 * instant/local/no-confirm) - this panel is for seats that already exist on
 * the server (seat_geometries collection). Deleting here always calls the
 * canonical DELETE /api/seat-map/seat-geometries/{id} endpoint, which the
 * backend governs (blocks with 409 if actively assigned, or approved/
 * published without first being set back to draft/calibrated).
 */
import React from 'react';

const STATUS_LABELS = {
  preview: 'Preview',
  draft: 'Draft',
  calibrated: 'Calibrated',
  approved: 'Approved',
  published: 'Published'
};

const SeatGeometryPropertiesPanel = ({
  seat,
  chartLabel,
  deleting = false,
  deleteError = null,
  onDelete,
  onClose,
  onUpdateStatus,
  statusUpdating = false,
  statusError = null
}) => {
  if (!seat) return null;

  const status = seat.geometry_status || 'draft';
  const statusLabel = STATUS_LABELS[status] || status;
  const assignmentLabel = seat.is_assigned
    ? (seat.ticket_reference || 'Assigned (no reference)')
    : 'None';
  const isGoverned = status === 'approved' || status === 'published';

  return (
    <div
      style={{
        position: 'absolute',
        top: '80px',
        right: '20px',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        zIndex: 500,
        width: '280px',
        maxHeight: 'calc(100vh - 160px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
      data-testid="seat-geometry-properties-panel"
    >
      {/* Header */}
      <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid #eee', flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>Seat Geometry</div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', padding: '4px' }}
            data-testid="close-seat-geometry-panel"
          >
            ×
          </button>
        </div>
        <div
          style={{
            display: 'inline-block',
            padding: '4px 10px',
            background: '#EEF2FF',
            color: '#4338CA',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: 600,
            textTransform: 'uppercase'
          }}
          data-testid="seat-geometry-status-badge"
        >
          {statusLabel}
        </div>
      </div>

      {/* Read-only details */}
      <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
        {[
          ['Chart', chartLabel || '—'],
          ['Section', seat.section],
          ['Row', seat.row],
          ['Seat', seat.seat],
          ['Status', statusLabel],
          ['Assignment', assignmentLabel]
        ].map(([label, value]) => (
          <div key={label} style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#666', marginBottom: '2px' }}>
              {label}
            </label>
            <div style={{ padding: '6px 10px', background: '#f5f5f5', borderRadius: '6px', fontSize: '13px' }}>
              {value ?? '—'}
            </div>
          </div>
        ))}

        <div style={{ fontSize: '11px', color: '#888', marginTop: '4px', lineHeight: 1.5 }}>
          Deleting this removes the map geometry only. It never deletes a ticket record.
        </div>

        {seat.is_assigned && (
          <div style={{ marginTop: '10px', fontSize: '11px', color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '6px', padding: '8px' }}>
            This seat has an active ticket assignment. Remove the assignment first before it can be deleted.
          </div>
        )}
        {!seat.is_assigned && isGoverned && (
          <div style={{ marginTop: '10px', fontSize: '11px', color: '#1D4ED8', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '6px', padding: '8px' }}>
            This seat is {statusLabel.toLowerCase()} and may be visible publicly. Set it back to Draft/Calibrated before it can be deleted.
          </div>
        )}
        {deleteError && (
          <div style={{ marginTop: '10px', fontSize: '12px', color: '#B91C1C', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '6px', padding: '8px' }} data-testid="seat-geometry-delete-error">
            {deleteError}
          </div>
        )}
        {statusError && (
          <div style={{ marginTop: '10px', fontSize: '12px', color: '#B91C1C', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '6px', padding: '8px' }} data-testid="seat-geometry-status-error">
            {statusError}
          </div>
        )}

        {/* Governed status progression - required before any assignment on
            this seat can ever become publicly visible (Publish Assignment
            checks that this geometry is Approved, then Published). */}
        <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid #eee' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#666', marginBottom: '8px' }}>
            Public Readiness
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {(status === 'draft' || status === 'calibrated') && (
              <button
                onClick={() => onUpdateStatus(seat.id, 'approved')}
                disabled={statusUpdating}
                data-testid="approve-seat-geometry-btn"
                style={{
                  padding: '9px', background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE',
                  borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: statusUpdating ? 'default' : 'pointer',
                  opacity: statusUpdating ? 0.6 : 1
                }}
              >
                {statusUpdating ? 'Updating…' : 'Approve Geometry'}
              </button>
            )}
            {status === 'approved' && (
              <>
                <button
                  onClick={() => onUpdateStatus(seat.id, 'published')}
                  disabled={statusUpdating}
                  data-testid="publish-seat-geometry-btn"
                  style={{
                    padding: '9px', background: '#16A34A', color: 'white', border: '1px solid #16A34A',
                    borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: statusUpdating ? 'default' : 'pointer',
                    opacity: statusUpdating ? 0.6 : 1
                  }}
                >
                  {statusUpdating ? 'Updating…' : 'Publish Geometry'}
                </button>
                <button
                  onClick={() => onUpdateStatus(seat.id, 'draft')}
                  disabled={statusUpdating}
                  data-testid="revert-seat-geometry-btn"
                  style={{
                    padding: '8px', background: 'white', color: '#6B7280', border: '1px solid #D1D5DB',
                    borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: statusUpdating ? 'default' : 'pointer',
                    opacity: statusUpdating ? 0.6 : 1
                  }}
                >
                  Revert to Draft
                </button>
              </>
            )}
            {status === 'published' && (
              <>
                <div style={{ fontSize: '11px', color: '#047857' }}>
                  This geometry is Published - eligible for public-facing assignments.
                </div>
                <button
                  onClick={() => onUpdateStatus(seat.id, 'approved')}
                  disabled={statusUpdating}
                  data-testid="unpublish-seat-geometry-btn"
                  style={{
                    padding: '8px', background: 'white', color: '#6B7280', border: '1px solid #D1D5DB',
                    borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: statusUpdating ? 'default' : 'pointer',
                    opacity: statusUpdating ? 0.6 : 1
                  }}
                >
                  Unpublish (Keep Approved)
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ padding: '12px 20px 16px', borderTop: '1px solid #eee', flexShrink: 0 }}>
        <button
          onClick={() => onDelete(seat.id)}
          disabled={deleting}
          style={{
            width: '100%',
            padding: '10px',
            background: 'white',
            color: '#d44',
            border: '1px solid #d44',
            borderRadius: '6px',
            fontSize: '13px',
            cursor: deleting ? 'default' : 'pointer',
            opacity: deleting ? 0.6 : 1
          }}
          data-testid="delete-seat-geometry-btn"
        >
          {deleting ? 'Deleting…' : 'Delete Seat Geometry'}
        </button>
      </div>
    </div>
  );
};

export default SeatGeometryPropertiesPanel;
